import { IWorkspace } from '@publishing-engine/domain/models';
import { useQuery } from '@tanstack/react-query';
import React, { createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { fetchWorkspaceByShortname } from 'src/api/queries';
import Loading from 'src/pages/Status/Loading';
import NotFound from 'src/pages/Status/NotFound';
import Forbidden from 'src/pages/Status/Forbidden';
import { isForbiddenError, isUnauthorizedError } from 'src/utils/queryClient';

export type IWorkspaceContextData = {
  workspace: Pick<IWorkspace, 'id' | 'organizationId' | 'featureConfig'>;
  isLoading: boolean;
};

export const WorkspaceContext = createContext<IWorkspaceContextData | undefined>(
  undefined
);

export const useWorkspaceData = (): IWorkspaceContextData => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspaceData must be used within a WorkspaceProvider');
  }
  return context;
};

export interface IWorkspaceLayoutProps {
  children: React.ReactNode;
}

const WorkspaceProvider = ({ children }: IWorkspaceLayoutProps) => {
  const { shortname = '' } = useParams();

  const {
    data: workspace,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['workspace', { shortname }],
    queryFn: () =>
      shortname ? fetchWorkspaceByShortname(shortname) : null,
    retry: 0,
    enabled: !!shortname
  });

  // Error handling: prioritize authorization errors
  if (!isLoading && isError) {
    return isUnauthorizedError(error) || isForbiddenError(error)
      ? <Forbidden />
      : <NotFound />;
  }

  // Show loading while fetching
  if (isLoading) {
    return <Loading />;
  }

  // Check if workspace data is empty after loading completes
  if (!workspace) {
    return <NotFound />;
  }

  return (
    <WorkspaceContext.Provider value={{ workspace, isLoading }}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceProvider;
